<template>
  <div>
    <div class="d-flex justify-content-between mb-3">
      <h1 v-t="'entities.manufacturingItem.name_plural'" class="mt-0"></h1>

      <div>
        <el-switch
          v-if="allowEditing"
          v-model="editModeToggle"
          :active-text="t.editModeToggle.batchMode"
          class="mr-3"
        />
        <el-button @click="refresh" v-t="'common.refresh'"/>
      </div>
    </div>

    <scan-dialog
      v-if="isCommissioner"
      :visible.sync="scanDialog.visible"
      :manufacturing-item-id="scanDialog.manufacturingItemId"
      :status="finalStatus"
      @pushed:status="onPushedStatus"
    />

    <create-manufacturing-item-dialog
      v-if="isCommissioner"
      :visible.sync="createManufacturingItemDialog.visible"
      @created:manufacturing-item="onCreatedManufacturingItem"
    />

    <dx-data-grid
      ref="dataGrid"
      :data-source="store"
      :remote-operations="remoteOperations"
      :columns="columns"
      :column-auto-width="true"
      :filter-value="filterValue"
      :show-borders="true"
      :show-row-lines="false"
      :show-column-lines="true"
      :row-alternation-enabled="true"
      @toolbar-preparing="onToolbarPreparing"
      @row-prepared="onRowPrepared"
    >
      <dx-editing
        :allow-updating="allowEditing"
        :mode="editMode"
        :form="{colCount: 4}"
      />

      <dx-column-chooser :enabled="true" mode="select" :height="500"/>

      <dx-export :enabled="true" :file-name="getExportFileName()"/>

      <dx-sorting mode="multiple"/>

      <dx-filter-row :visible="true"/>
      <dx-filter-panel :visible="true"/>

      <dx-paging :page-size="20"/>
      <dx-pager
        :allowed-page-sizes="[15, 20, 50]"
        :visible="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :show-page-size-selector="true"
      />
    </dx-data-grid>
  </div>
</template>

<script>
import { DxDataGrid } from 'devextreme-vue';
import {
  DxColumnChooser,
  DxEditing,
  DxExport,
  DxFilterPanel,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxSorting,
} from 'devextreme-vue/data-grid';
import { get, omitBy } from 'lodash';
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';
import store from '@/store';
import CreateManufacturingItemDialog from '@/components/CreateManufacturingItemDialog.vue';
import addRowPriorityClass from '@/lib/dx/add-row-priority-class';
import { buildColumns } from '@/lib/data-grid-columns';
import createBackendStore from '@/lib/create-backend-store';
import ScanDialog from '@/components/ScanDialog.vue';

export default {
  name: 'ManufacturingItems',
  components: {
    CreateManufacturingItemDialog,
    DxColumnChooser,
    DxDataGrid,
    DxEditing,
    DxExport,
    DxFilterRow,
    DxFilterPanel,
    DxPager,
    DxPaging,
    DxSorting,
    ScanDialog,
  },
  data() {
    return {
      columns: [],
      store: null,
      remoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      editModeToggle: false,
      scanDialog: {
        visible: false,
        manufacturingItemId: NaN,
      },
      createManufacturingItemDialog: {
        visible: false,
      },
    };
  },
  props: {
    filterValue: {
      type: Array,
      get default() {
        // using getter property to delay evaluation because statuses first need to be fetched

        let value;

        if (store.getters.isCommissioner) {
          const { statuses } = store.state;
          const canceled = statuses.find(s => s.name === 'canceled');
          const final = statuses.find(s => s.name === 'final');
          const filter = [['statusId', '<>', canceled.id], ['statusId', '<>', final.id]];

          value = () => filter;
        } else {
          // backend already scopes status
          value = null;
        }

        // memoize value
        Object.defineProperty(this, 'default', { value });

        return value;
      },
    },
    nextStatusId: Number,
  },
  computed: {
    ...mapState(['statuses', 'user']),
    ...mapGetters(['isCommissioner', 'isManufacturingAdmin']),
    t() {
      return {
        editModeToggle: {
          batchMode: this.$t('common.batchMode'),
        },
      };
    },
    allowEditing() {
      return this.isCommissioner || this.isManufacturingAdmin;
    },
    dataGrid() {
      return this.$refs.dataGrid.instance;
    },
    editMode() {
      return this.editModeToggle ? 'batch' : 'form';
    },
    finalStatus() {
      return this.statuses.find(status => status.name === 'final');
    },
  },
  methods: {
    buildColumns,
    configureColumns() {
      return [
        ...this.buildColumns('manufacturingItem', [
          {
            property: 'id',
          },
          {
            property: 'orderItemEid',
            captionContext: 'short',
            allowEditing: false,
            visible: false,
          },
          {
            property: 'shipment.orderEid',
            entity: 'shipment',
            captionContext: 'short',
            allowEditing: false,
          },
          {
            property: 'shipment.acceptanceNumber',
            entity: 'shipment',
            captionContext: 'short',
            allowEditing: false,
          },
          {
            property: 'priority',
            allowEditing: this.isCommissioner,
            sortOrder: 'desc',
            sortIndex: 0,
          },
          {
            property: 'statusId',
            allowEditing: this.allowEditing,
          },
          {
            property: 'statusEventMap',
            alignment: 'center',
            options: {
              columns: status => ({
                allowEditing: this.allowEditing,
                visible: status.name !== 'final',
                width: 65,
              }),
            },
          },
          {
            property: 'packageCount',
            captionContext: 'short',
          },
          {
            property: 'orderedAt',
            captionContext: 'short',
            sortOrder: 'asc',
            sortIndex: 2,
            allowEditing: false,
          },
          {
            property: 'complaintTypeId',
            allowEditing: this.isCommissioner,
            captionContext: 'short',
          },
          {
            property: 'manufacturingDefect',
            allowEditing: this.isCommissioner,
            visible: false,
          },
          {
            property: 'quantity',
            allowEditing: this.isCommissioner,
          },
          {
            property: 'productNameId',
            captionContext: 'short',
            allowEditing: this.isCommissioner,
          },
          {
            property: 'productCategoryId',
            captionContext: 'short',
            allowEditing: this.isCommissioner,
          },
          {
            property: 'shipment.customer',
            entity: 'shipment',
            allowEditing: false,
          },
          {
            property: 'shipment.destinationCountryCode',
            entity: 'shipment',
            allowEditing: false,
          },
          ...this.isCommissioner ? [
            {
              property: 'materialId',
              visible: false,
            },
            {
              property: 'lamellaTypeId',
              visible: false,
            },
            {
              property: 'plateThickness',
              visible: false,
            },
            {
              property: 'legProfile',
              visible: false,
            },
          ] : [],
          {
            property: 'deliverToId',
            allowEditing: this.isCommissioner,
          },
          {
            property: 'planWeek',
            captionContext: 'short',
            sortOrder: 'asc',
            sortIndex: 1,
            minWidth: 50,
            allowEditing: false,
          },
          ...this.isCommissioner ? [{
            property: 'manufacturerId',
          }] : [],
        ]),
        ...this.allowEditing ? [{
          type: 'buttons',
          width: this.isCommissioner ? 61 : 40,
          alignment: this.isCommissioner ? 'left' : 'center',
          buttons: [
            'edit',
            ...this.isCommissioner ? [
              {
                icon: 'fas fa-check-square',
                hint: this.$t('views.manufacturingItems.finalizeHint'),
                onClick: event => this.onClickFinalize(event.row.key),
                visible: ({ row }) => {
                  if (row.data.orderItemEid) return false;

                  const status = this.statuses.find(s => s.id === row.data.statusId);
                  return !status || !['final', 'canceled'].includes(status.name);
                },
              },
            ] : [],
          ],
        }] : [],
      ];
    },
    configureStore(columns) {
      const fields = columns
        .map(column => column.dataField)
        .filter(f => f && !f.startsWith('statusEventMap'));
      const relations = this.statuses
        .map(s => `statusEventMap[${s.id}]`);

      return createBackendStore('/manufacturing-items', {
        fields,
        relations,
        valuesFilter(values) {
          const effectiveValues = omitBy(values, (value, key) => key.startsWith('statusEventMap'));
          const { statusId, statusEventMap } = values;

          if (statusEventMap && statusId && statusEventMap.hasOwnProperty(statusId)) {
            effectiveValues.skipStatusEventUpdate = true;
          }

          return effectiveValues;
        },
        afterInsertOrUpdate: async (key, values) => {
          if (!values.statusEventMap) return;

          const row = this.dataGrid.getDataSource()
            .items()
            .find(item => item.id === key);

          const promises = Object.entries(values.statusEventMap)
            .map(async ([statusIdString, statusEventValues]) => {
              const statusId = parseInt(statusIdString, 10);
              const statusEvent = get(row, ['statusEventMap', statusId]);

              if (statusEventValues.occurredAt) {
                if (statusEvent) {
                  await this.$http.patch(`/status-events/${statusEvent.id}`, statusEventValues);
                } else {
                  await this.$http.post('/status-events', {
                    ...statusEventValues,
                    manufacturingItemId: key,
                    statusId,
                  });
                }
              } else if (statusEvent) {
                await this.$http.delete(`/status-events/${statusEvent.id}`);
              }
            });

          await Promise.all(promises);
        },
      });
    },
    showCreateManufacturingItemDialog() {
      this.createManufacturingItemDialog.visible = true;
    },
    getExportFileName() {
      const timestamp = moment()
        .format('YYYY-MM-DD_HH-mm-ss');
      return `manufacturing-items_${timestamp}`;
    },
    refresh() {
      this.dataGrid.refresh();
    },
    onClickFinalize(manufacturingItemId) {
      this.scanDialog.manufacturingItemId = manufacturingItemId;
      this.scanDialog.visible = true;
    },
    onCreatedManufacturingItem() {
      this.refresh();
    },
    onPushedStatus() {
      this.refresh();
    },
    onRowPrepared: addRowPriorityClass,
    onToolbarPreparing(event) {
      if (!this.isCommissioner) return;

      event.toolbarOptions.items.push({
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'add',
          onClick: this.showCreateManufacturingItemDialog,
        },
      });
    },
  },
  created() {
    this.columns = this.configureColumns();
    this.store = this.configureStore(this.columns);
  },
  mounted() {
    this.$emit('requestLayoutWidthMode', 'full');

    if (this.isCommissioner) {
      // set certain fields to read-only if associated with an order

      const dataFields = [
        'complaintTypeId',
        'deliverToId',
        'lamellaTypeId',
        'legProfile',
        'manufacturerId',
        'materialId',
        'plateThickness',
        'productCategoryId',
        'productNameId',
        'quantity',
      ];

      this.dataGrid.on('editorPreparing', (event) => {
        if (!event.row) return;

        const { statusId, orderItemEid } = event.row.data;

        if (event.dataField === 'statusId') {
          if (!orderItemEid) return;

          const status = this.statuses.find(s => s.id === statusId);
          const protectedStatuses = ['final', 'canceled'];

          if (protectedStatuses.includes(status.name)) {
            // eslint-disable-next-line no-param-reassign
            event.editorOptions.readOnly = true;
          } else {
            // We manipulate the editor's data source without changing the original.
            const dataSource = Object.create(event.editorOptions.dataSource);
            dataSource.store = Object.create(dataSource.store);
            // eslint-disable-next-line no-underscore-dangle
            dataSource.store._array = dataSource.store._array
              .filter(s => !protectedStatuses.includes(s.name));

            // eslint-disable-next-line no-param-reassign
            event.editorOptions.dataSource = dataSource;
          }
        } else if (dataFields.includes(event.dataField)) {
          // eslint-disable-next-line no-param-reassign
          event.editorOptions.readOnly = !!orderItemEid;
        }
      });
    }
  },
};
</script>

<style scoped>

</style>
